import { useMemo, useState, useRef, useEffect } from "react";
import { Search, X, ChevronRight, Lightbulb, ExternalLink, Pencil } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl } from "@/lib/api";

const imgSrc = (img) =>
  img && (img.startsWith("/api/") || img.startsWith("http")) ? mediaUrl(img) : img;

const highlight = (text, q) => {
  if (!q) return text;
  const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-warm/70 text-ink px-0.5">{p}</mark>
      : p
  );
};

const matches = (s, q) => {
  if (!q) return true;
  const hay = [s.label, s.summary, ...(s.steps || []), ...(s.tips || [])].join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
};

export default function AdminGuide() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const refs = useRef({});

  useEffect(() => {
    api.get("/guide/sections").then(({ data }) => setAll(data)).finally(() => setLoading(false));
  }, []);

  const sections = useMemo(() => all.filter((s) => matches(s, q)), [all, q]);

  useEffect(() => {
    if (sections.length && !active) setActive(sections[0].id);
  }, [sections, active]);

  // Deep-link: ?from=/admin/orders → jump to that page's section
  useEffect(() => {
    const from = params.get("from");
    if (!from || !all.length) return;
    const match =
      all.find((s) => s.route === from) ||
      all
        .filter((s) => s.route && from.startsWith(s.route))
        .sort((a, b) => b.route.length - a.route.length)[0];
    if (match) {
      setActive(match.id);
      setTimeout(() => refs.current[match.id]?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
    }
    params.delete("from");
    setParams(params, { replace: true });
  }, [all]); // eslint-disable-line

  const jump = (id) => {
    setActive(id);
    refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="admin-guide">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl">Admin Guide</h1>
          <p className="font-body text-sm text-ink/50 mt-1">
            A step-by-step walkthrough of every section of your console, with live screenshots. Search anything below.
          </p>
        </div>
        {user?.role === "super_admin" && (
          <Link to="/admin/guide-editor" data-testid="guide-edit-link"
            className="flex-none inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 font-body text-[0.62rem] uppercase tracking-wide2 hover:bg-ink hover:text-linen transition-colors">
            <Pencil size={13} /> Edit Guide
          </Link>
        )}
      </div>

      <div className="relative mb-8 max-w-xl">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the guide — e.g. tracking, promo code, VIP gate, refund…"
          className="w-full bg-cream border border-ink/15 pl-11 pr-10 py-3 font-body text-sm focus:outline-none focus:border-ink/50 transition-colors"
          data-testid="guide-search"
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink" data-testid="guide-search-clear">
            <X size={16} />
          </button>
        )}
      </div>

      {loading ? (
        <p className="font-body text-sm text-ink/40">Loading guide…</p>
      ) : (
        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          <nav className="hidden lg:block self-start sticky top-24" data-testid="guide-index">
            <p className="font-body uppercase tracking-luxe text-[0.55rem] text-ink/40 px-3 pb-3">Sections</p>
            <div className="space-y-0.5">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => jump(s.id)}
                  data-testid={`guide-jump-${s.id}`}
                  className={`w-full text-left flex items-center justify-between px-3 py-2 font-body text-[0.72rem] tracking-wide2 transition-colors ${active === s.id ? "bg-ink text-linen" : "text-ink/70 hover:bg-warm/40"}`}
                >
                  {highlight(s.label, q)}
                  <ChevronRight size={13} className="opacity-40" />
                </button>
              ))}
              {!sections.length && <p className="px-3 font-body text-xs text-ink/40">No matches.</p>}
            </div>
          </nav>

          <div className="space-y-16 min-w-0">
            {sections.length === 0 && (
              <div className="adm-card text-center py-16" data-testid="guide-empty">
                <p className="font-heading text-2xl text-ink/40">Nothing matches “{q}”.</p>
                <p className="font-body text-sm text-ink/50 mt-2">Try a simpler word like “order”, “image” or “email”.</p>
              </div>
            )}

            {sections.map((s, idx) => (
              <section
                key={s.id}
                ref={(el) => (refs.current[s.id] = el)}
                className="scroll-mt-24"
                data-testid={`guide-section-${s.id}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-heading text-2xl text-ink/25 tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                  <div>
                    <h2 className="font-heading text-2xl">{highlight(s.label, q)}</h2>
                    {s.route && (
                      <Link to={s.route} className="inline-flex items-center gap-1 font-body text-[0.62rem] uppercase tracking-wide2 text-ink/45 hover:text-ink mt-1 transition-colors" data-testid={`guide-open-${s.id}`}>
                        Open {s.label} <ExternalLink size={11} />
                      </Link>
                    )}
                  </div>
                </div>

                <p className="font-body text-sm text-ink/70 leading-relaxed mb-5 max-w-2xl">{highlight(s.summary, q)}</p>

                {s.image && (
                  <div className="border hairline bg-cream overflow-hidden mb-6">
                    <img
                      src={imgSrc(s.image)}
                      alt={`${s.label} admin screen`}
                      loading="lazy"
                      className="w-full block"
                      onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                      data-testid={`guide-shot-${s.id}`}
                    />
                  </div>
                )}

                <ol className="space-y-3 mb-6">
                  {(s.steps || []).map((step, i) => (
                    <li key={i} className="flex gap-3 max-w-2xl">
                      <span className="flex-none w-6 h-6 rounded-full bg-ink text-linen font-body text-[0.7rem] flex items-center justify-center mt-0.5">{i + 1}</span>
                      <span className="font-body text-sm text-ink/80 leading-relaxed">{highlight(step, q)}</span>
                    </li>
                  ))}
                </ol>

                {s.tips?.length > 0 && (
                  <div className="bg-warm/30 border-l-2 border-ink/40 px-5 py-4 max-w-2xl">
                    {s.tips.map((tip, i) => (
                      <p key={i} className="flex gap-2 font-body text-[0.8rem] text-ink/70 leading-relaxed [&:not(:first-child)]:mt-2">
                        <Lightbulb size={14} className="flex-none mt-0.5 text-ink/50" /> {highlight(tip, q)}
                      </p>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
